import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { config } from 'application';
import { getMessages } from 'application/translation';
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import { matchPath } from 'react-router-dom';
import { langs } from 'utils/langs';
import { DEFAULT_LANG_PARAM_NAME } from 'utils/pathFactory';

export type LangContextValue = [string, string, (lang: string) => void];
export const LangContext = React.createContext<LangContextValue | undefined>(undefined);

export const IntlProvider: React.FC<{ children: ReactNode }> = props => {
  const { inProgress } = useMsal();
  const [lang, setLang] = useState<string>(config.defaultLanguage);
  const [messages, setMessages] = useState<Record<string, string> | undefined>();
  const context = useMemo<LangContextValue>(
    () => [lang, langs.find(item => item.code === lang)!.dir, setLang],
    [lang, setLang],
  );

  useEffect(() => {
    const langPathPattern = `${config.publicUrl}/${DEFAULT_LANG_PARAM_NAME}/*`;
    const match = matchPath(langPathPattern, window.location.pathname);

    if (match && match.params.lang) {
      setLang(match.params.lang);
    } else if (inProgress === InteractionStatus.None) {
      window.history.pushState(null, '', `${config.publicUrl}/${config.defaultLanguage}/`);
    }
  }, [inProgress]);

  useEffect(() => {
    getMessages().then(messages => setMessages(messages));
  }, []);

  return (
    <LangContext.Provider value={context}>
      <ReactIntlProvider locale={lang} defaultLocale={config.defaultLanguage} messages={messages} {...props} />
    </LangContext.Provider>
  );
};

export const useLang = () => {
  const context = useContext(LangContext);

  if (!context) {
    throw new Error('useLang must be used within a IntlProvider');
  }

  const [lang, dir, setLang] = context;

  return { lang, dir, setLang };
};
